import React, { MutableRefObject, useCallback, useEffect, useRef } from 'react';

export interface IImgPlaceholder extends React.ComponentProps<'img'> {
  placeholder: string;
}

const ImgPlaceholder: React.FC<IImgPlaceholder> = ({ src, placeholder, ...props }) => {
  // @ts-ignore
  const ref: MutableRefObject<HTMLImageElement> = useRef(null);
  const refCount: MutableRefObject<number> = useRef(0);

  // Error happened/catched after hydration
  const onError: React.ReactEventHandler<HTMLImageElement> = useCallback(
    (evt: React.SyntheticEvent<HTMLImageElement, Event>) => {
      // If PLACEHOLDER URL is WRONG (=404), we should avoid an INFINITE LOOP!
      if (refCount.current === 0) {
        const img: HTMLImageElement = evt.target as HTMLImageElement;
        img.src = placeholder;
        img.removeAttribute('data-placeholder');
        // eslint-disable-next-line no-console
        console.warn(
          `[livedata-sports-design-system] Missing image '${src}', replaced with placeholder '${placeholder}'`
        );
        refCount.current++;
      } else {
        // PLACEHOLDER URL is WRONG (=404)!
        console.error(`[livedata-sports-design-system] Placeholder URL '${placeholder}' not found!`); // eslint-disable-line no-console
      }
    },
    [placeholder] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Error happened before hydration, but catched after hydration
  useEffect(() => {
    if (ref && ref.current) {
      const { complete, naturalHeight } = ref.current;
      const errorLoadingImgBeforeHydration = complete && naturalHeight === 0;

      if (errorLoadingImgBeforeHydration) {
        ref.current.src = placeholder;
        ref.current.removeAttribute('data-placeholder');
        // eslint-disable-next-line no-console
        console.warn(
          `[livedata-sports-design-system] SSR - Missing image '${src}', replaced with placeholder '${placeholder}'`,
          { ref, complete, naturalHeight }
        );
      }
    }
  }, [placeholder]); // eslint-disable-line react-hooks/exhaustive-deps

  return <img ref={ref} src={src} {...props} data-placeholder={placeholder} onError={onError} />;
};

export default ImgPlaceholder;
